<template>
    <v-app-bar app>
        <v-app-bar-nav-icon v-if="smAndDown" aria-label="Open menu" @click="toggleDrawer"/>
        <!-- Opens the Ctrl/Cmd+K search; icon only on phones -->
        <v-btn v-if="smAndDown" icon="mdi-magnify" aria-label="Search pages and vehicles" @click="openPalette"/>
        <button v-else type="button" class="search-pill ms-4" aria-haspopup="dialog"
                :aria-label="`Search pages and vehicles (${shortcutLabel})`" @click="openPalette">
            <v-icon size="20" class="text-medium-emphasis">mdi-magnify</v-icon>
            <span class="search-pill-text text-medium-emphasis">Search pages and vehicles</span>
            <kbd class="search-pill-kbd">{{ shortcutLabel }}</kbd>
        </button>

        <v-spacer/>

        <v-btn v-if="isCompanyAdmin && subscriptionStore.needsAttention"
               class="me-2" icon :aria-label="subscriptionStore.statusMessage || 'Billing'" @click="navigateTo('invoices')">
            <v-icon size="large" :color="subscriptionStore.isOverdue ? 'red' : 'amber'">
                mdi-credit-card-alert
            </v-icon>
            <v-tooltip activator="parent" location="bottom">
                {{ subscriptionStore.statusMessage }}
            </v-tooltip>
        </v-btn>

        <v-btn class="me-2" :class="{'sonar-btn': announcementsStore.unreadCount > 0}"
               icon aria-label="Updates and offers" @click="announcementsOpen = !announcementsOpen">
            <v-icon size="large">mdi-bullhorn-outline</v-icon>
            <v-badge v-if="announcementsStore.unreadCount" color="primary"
                     offset-y="-10" overlap>
                <template #badge>
                    {{ announcementsStore.unreadCount < 99 ? announcementsStore.unreadCount : '99+' }}
                </template>
            </v-badge>
            <v-tooltip activator="parent" location="bottom">Updates & Offers</v-tooltip>
        </v-btn>

        <v-btn class="me-2" icon :aria-label="notificationsCount ? `Alerts, ${notificationsCount} unread` : 'Alerts'"
               @click="navigateTo('notifications')">
            <v-icon size="large">mdi-bell</v-icon>
            <v-badge v-if="notificationsCount" color="red" offset-y="-10" overlap>
                <template #badge>{{ notificationsCount < 99 ? notificationsCount : '99+' }}</template>
            </v-badge>
        </v-btn>

        <AnnouncementsPanel :open="announcementsOpen" @close="announcementsOpen = false"/>

        <v-menu v-if="authStore.portals?.length"
                :close-on-content-click="false"
                class="me-2"
                open-on-hover>
            <template v-slot:activator="{ props }">
                <v-btn icon v-bind="props" aria-label="Apps"
                       @click="navigateTo('sso')">
                    <v-icon size="large">mdi-apps</v-icon>
                </v-btn>
            </template>

            <v-list>
                <v-list-item
                    v-for="portal in authStore.portals"
                    :title="portal.name"
                    @click="switchPortal(portal)"/>
            </v-list>
        </v-menu>

        <v-menu v-if="authStore.user" :close-on-content-click="false">
            <template v-slot:activator="{ props }">
                <v-btn icon size="small" v-bind="props" aria-label="Account menu">
                    <UserAvatar :src="authStore.user.avatar" :first-name="authStore.user.first_name"
                                :last-name="authStore.user.last_name" :email="authStore.user.email" size="small"/>
                </v-btn>
            </template>

            <v-list width="250">
                <v-list-group v-if="authStore.user.type !== 'admin' && authStore.user?.companies?.length">
                    <template v-slot:activator="{ props }">
                        <v-list-item title="Switch Company" v-bind="props"/>
                    </template>

                    <v-list v-for="company in authStore.user.companies" :key="company.id"
                            class="bg-grey-lighten-4">
                        <v-list-item :title="company.company_name" @click="switchCompany(company.id)"/>
                    </v-list>
                </v-list-group>

                <v-list-item title="Profile" @click="navigateTo('profile')"/>
                <v-list-item v-if="authStore.user.type !== 'admin'" title="Preferences" @click="navigateTo('settings')"/>
                <v-list-item :title="`Logout (${authStore.user.first_name})`" @click="logout"/>
            </v-list>
        </v-menu>
    </v-app-bar>
</template>

<script setup>
import {ref, computed, inject, onMounted, onBeforeUnmount} from "vue";
import {useDisplay} from "vuetify";
import router from "@/router";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useNavigation, isCompanyAdminRole} from "@/composables/globals";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {unreadNotificationsCount} from "@/composables/apiClient";
import {useSubscriptionStore} from "@/stores/subscriptionStore";
import {useAnnouncementsStore} from "@/stores/announcementsStore";
import UserAvatar from "@/components/UserAvatar.vue";
import AnnouncementsPanel from "@/components/announcements/AnnouncementsPanel.vue";

const {navigateTo} = useNavigation();
const snackbar = useSnackbarStore();
const authStore = useAuthStore();
const subscriptionStore = useSubscriptionStore();
const announcementsStore = useAnnouncementsStore();
const notificationsCount = ref(unreadNotificationsCount);
const announcementsOpen = ref(false);
const {smAndDown} = useDisplay();
const navDrawerOpen = inject('navDrawerOpen', ref(false));
const toggleDrawer = () => navDrawerOpen.value = !navDrawerOpen.value;
const paletteOpen = inject('paletteOpen', ref(false));
const openPalette = () => paletteOpen.value = true;
const shortcutLabel = (typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)) ? 'Cmd K' : 'Ctrl K';

const isCompanyAdmin = computed(() => isCompanyAdminRole(authStore.user));

const switchCompany = (company) => {
    send('post', `switch-company/${company}`)
        .then((response) => {
            if (response.data.success) {
                if (response.data.data.user) {
                    authStore.updateAuth(response.data.data);
                }
                router.go();
            } else {
                snackbar.showSnackbar(response.data.message ?? 'Something went wrong', 'error')
            }
        })
        .catch((error) => {
            snackbar.showSnackbar(error.response.data.message ?? 'Something went wrong', 'error')
        })
}

const switchPortal = (portal) => {
    // Open a blank page immediately to ensure the new tab is opened
    const newTab = window.open("", "_blank");

    send('post', `switch-portal/${portal.type}`)
        .then((response) => {
            if (response.data.success) {
                // Update the blank page's location to the desired URL
                newTab.location.href = `${response.data.data.url}`;
            } else {
                snackbar.showSnackbar(response.data.message ?? 'Something went wrong', 'error');
                // Close the tab if the operation fails
                newTab.close();
            }
        })
        .catch((error) => {
            snackbar.showSnackbar(error.response?.data?.message ?? 'Something went wrong', 'error');
            // Close the tab if an error occurs
            newTab.close();
        });
}

const logout = () => {
    authStore.logout();
}

// Keep the notification badge fresh while the user sits idle on a page.
let unreadPoll = null;
let announcementsPoll = null;
onMounted(() => {
    unreadPoll = setInterval(() => {
        send('get', 'notifications/unread-count').catch(() => {});
    }, 60000);

    announcementsStore.fetchMessages();
    announcementsPoll = setInterval(() => {
        announcementsStore.fetchMessages();
    }, 300000);
});
onBeforeUnmount(() => {
    if (unreadPoll) clearInterval(unreadPoll);
    if (announcementsPoll) clearInterval(announcementsPoll);
});
</script>

<style scoped>
@keyframes sonar-ring {
    0% { box-shadow: 0 0 0 0 rgba(var(--v-theme-primary), 0.4); }
    70% { box-shadow: 0 0 0 8px rgba(var(--v-theme-primary), 0); }
    100% { box-shadow: 0 0 0 0 rgba(var(--v-theme-primary), 0); }
}

.search-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 280px;
    max-width: 420px;
    height: 38px;
    padding: 0 8px 0 12px;
    border-radius: 19px;
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    background: rgba(var(--v-theme-on-surface), 0.04);
    cursor: pointer;
    text-align: left;
}

.search-pill:hover {
    background: rgba(var(--v-theme-on-surface), 0.08);
}

.search-pill:focus-visible {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: 2px;
}

.search-pill-text {
    flex: 1;
    font-size: 0.875rem;
    white-space: nowrap;
}

.search-pill-kbd {
    font-family: inherit;
    font-size: 0.75rem;
    padding: 1px 6px;
    border-radius: 4px;
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.sonar-btn {
    animation: sonar-ring 2s ease-out infinite;
    border-radius: 50%;
}
</style>
